import { clsx } from '@mantine/styles'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { sanitize } from 'isomorphic-dompurify'

import { Flex, Typography } from '@percent/lemonade'
import type { EmployeeAchievementsResponse } from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import styles from './Achievement.module.scss'
import { HistoryListItem } from '@percent/partner-dashboard/common/components/historyListItem/HistoryListItem'

function AchievedAt({ achievedAt, assignedBy }: Readonly<{ achievedAt: string; assignedBy: string | null }>) {
  const { t } = useTranslation()

  const copy = assignedBy
    ? t('typography.employeeDetails.rewards.assignedBy', {
        date: format(new Date(achievedAt), 'MMM d, yyyy'),
        assignedBy
      })
    : t('typography.employeeDetails.rewards.achievedAt', { date: format(new Date(achievedAt), 'MMM d, yyyy') })

  return <span className={styles.achievedAt}>{copy}</span>
}

export type AchievementProps = Readonly<{
  achievement: EmployeeAchievementsResponse['rulesAndAchievements'][number]
}>

export function Achievement({ achievement }: AchievementProps) {
  const { t } = useTranslation()
  const achieved = achievement.accountAchievement !== null

  return (
    <HistoryListItem markAsFinished={achieved}>
      <Flex direction="column" className={styles.detailsWrapper}>
        {achievement.accountAchievement && (
          <AchievedAt
            achievedAt={achievement.accountAchievement.createdAt}
            assignedBy={achievement.accountAchievement.assignedBy}
          />
        )}
        <Typography variant="default-bold" $color={achieved ? 'gray300' : 'text_primary'}>
          {achievement.achievement.name}
        </Typography>
        {achievement.rule.conditions.description && (
          <p className={styles.detailsDescription}>{achievement.rule.conditions.description}</p>
        )}
        <Flex direction="column" className={styles.stepsWrapper}>
          {achievement.rule.conditions.conditions?.map(
            ({ factKind, operator, currentValue, description, descriptionLines, pass }) => {
              const desc = descriptionLines ? descriptionLines.join(' or ') : description

              return (
                desc && (
                  <div
                    key={`${operator}-${factKind}-${desc}`}
                    className={clsx(styles.step, { [styles.checked]: pass })}
                    data-testid="step"
                  >
                    <div className={clsx(styles.stepIcon, { [styles.checked]: pass })} />
                    <p
                      dangerouslySetInnerHTML={{
                        __html: sanitize(desc, { ALLOWED_TAGS: ['p', 'br'] })
                      }}
                    />
                    {achievement.rule.conditions.operator === 'nPass' && currentValue !== undefined && (
                      <Typography variant="default-bold" $color="gray300" className={styles.currentValue}>
                        {t('typography.employeeDetails.rewards.numberCompleted', { currentValue })}
                      </Typography>
                    )}
                  </div>
                )
              )
            }
          )}
        </Flex>
      </Flex>
    </HistoryListItem>
  )
}
